import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn'
import { useScrollFade } from '@/hooks/useScrollFade'

export interface SegmentedOption<T extends string> {
  value: T
  label: ReactNode
  /** Rendered before the label, e.g. an AthleteDot or SportBadge dot. */
  icon?: ReactNode
}

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedOption<T>[]
  value: T
  onChange: (value: T) => void
  'aria-label': string
  className?: string
}

/** Radio group drawn as a pill track with a raised thumb that glides to the chosen option. */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  className,
  ...props
}: SegmentedControlProps<T>) {
  const groupRef = useRef<HTMLDivElement>(null)
  const [thumb, setThumb] = useState<{ left: number; width: number } | null>(null)
  // The first placement snaps; only later moves glide, so the thumb never flies in from 0.
  const [settled, setSettled] = useState(false)

  useLayoutEffect(() => {
    const group = groupRef.current
    const active = group?.querySelector<HTMLElement>('[aria-checked="true"]')
    if (!group || !active) {
      setThumb(null)
      return
    }
    const measure = () => setThumb({ left: active.offsetLeft, width: active.offsetWidth })
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(group)
    return () => observer.disconnect()
  }, [value, options])

  useScrollFade(groupRef)

  // When the row scrolls sideways (a phone), bring the chosen option into view.
  useLayoutEffect(() => {
    const group = groupRef.current
    const active = group?.querySelector<HTMLElement>('[aria-checked="true"]')
    if (!group || !active || group.scrollWidth <= group.clientWidth) return
    const left = active.offsetLeft - 16
    const right = active.offsetLeft + active.offsetWidth + 16 - group.clientWidth
    if (group.scrollLeft > left) group.scrollTo({ left, behavior: 'smooth' })
    else if (group.scrollLeft < right) group.scrollTo({ left: right, behavior: 'smooth' })
  }, [value])

  useLayoutEffect(() => {
    if (!thumb || settled) return
    const id = requestAnimationFrame(() => setSettled(true))
    return () => cancelAnimationFrame(id)
  }, [thumb, settled])

  // jsdom (tests) measures everything as zero; keep the static active style there.
  const showThumb = thumb != null && thumb.width > 0

  // The track keeps its border whole; only the options inside it scroll and fade at the edge, so
  // a phone shows "swipe for more" rather than a control cut off at the screen edge.
  return (
    <div
      className={cn(
        'inline-flex max-w-full rounded-md border border-line bg-surface-2 p-0.5',
        className,
      )}
    >
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={props['aria-label']}
        className="gd-scroll-x relative flex min-w-0 items-center gap-0.5"
      >
        {showThumb && (
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-y-0 left-0 rounded-sm bg-surface-pop shadow-thumb',
              settled && 'transition-[transform,width] duration-300 ease-[var(--ease-out)]',
            )}
            style={{ width: thumb.width, transform: `translateX(${thumb.left}px)` }}
          />
        )}
        {options.map((option) => {
          const active = option.value === value
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(option.value)}
              className={cn(
                'relative inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-sm px-2.5 py-1 text-small font-medium transition-[color,transform] duration-150 active:scale-[0.97] pointer-coarse:min-h-9 pointer-coarse:px-3',
                'focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent',
                active
                  ? cn('text-ink', !showThumb && 'bg-surface-pop shadow-thumb')
                  : 'text-ink-2 hover:text-ink',
              )}
            >
              {option.icon}
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
